import http from 'http'

import { callbackRoute } from './routes/callback'
import type { Route } from './types'

// The CLI's own HTTP server. Today it exists for one reason — receiving the authorization redirect that a
// browser cannot deliver any other way — but it is a route table rather than a single handler, so the next
// thing that needs a local endpoint adds a file under `routes/` and one entry to the array.
//
// It always listens on a random free port on localhost. A fixed port would collide with whatever else the
// developer is running, and binding anywhere but localhost would put an open endpoint on their network.

export { callbackRoute } from './routes/callback'
export type { Route, RouteContext } from './types'

export interface LocalServer {
  port: number
  close: () => void
}

export function startLocalServer(routes: Route[]): Promise<LocalServer> {
  return new Promise((resolve, reject) => {
    const server = http.createServer((req, res) => {
      const url = new URL(req.url || '/', 'http://localhost')
      const route = routes.find(entry => entry.method === req.method && entry.path === url.pathname)

      if (!route) {
        res.writeHead(404)
        res.end()

        return
      }

      route.handle({ req, res, url })
    })

    server.listen(0, 'localhost', () => {
      const address = server.address()

      if (!address || typeof address === 'string') {
        reject(new Error('Failed to start the local callback server'))

        return
      }

      resolve({
        port: address.port,
        // `close()` alone only stops listening: the browser's keep-alive socket would hold the event loop
        // open, which is survivable for a command that exits straight after but not for one that carries on
        // into a deploy.
        close: () => {
          server.closeAllConnections?.()
          server.close()
        },
      })
    })

    server.on('error', reject)
  })
}

export interface CallbackServer extends LocalServer {
  /** Resolves with the exchange code the console redirects back with. */
  waitForCode: () => Promise<string>
}

/** The one composition the login needs: a server whose only route is the authorization callback. */
export async function startCallbackServer(): Promise<CallbackServer> {
  const { route, waitForCode } = callbackRoute()
  const server = await startLocalServer([route])

  return { ...server, waitForCode }
}
